import React, { useState, useEffect } from "react";
import CommonModal from "../../../components/shared/modalLayout/CommonModal";
import { Row, Col } from "react-bootstrap";
import InputWithLabel from "../../../components/shared/fields/InputWithLabel";
import Button from "../../../components/shared/buttons/button";
import { toast } from "react-hot-toast";
import {
  useCreateManagerMutation,
  useUpdateManagerMutation,
} from "./managersApi";
import SelectBox from "../../../components/shared/fields/SelectBox ";
import { useGetRolesQuery } from "../../../app/globalApi";
import useTranslator from "../../../hooks/useTranslator";
import { capitalize, convertToUserTimezone, getCurrentDateTime } from "../../../helpers/Utils";
import { validatePhoneNumber } from "../../../helpers/phoneValidation";

const ManagerModal = ({ show, setShow, manager, mode }) => {
  const { _t } = useTranslator();
  const [createManager, { isLoading: isCreating }] = useCreateManagerMutation();
  const [updateManager, { isLoading: isUpdating }] = useUpdateManagerMutation();
  const { data: roleList } = useGetRolesQuery({ type: "staff" });

  const initialFormData = {
    name: "",
    email: "",
    mobile: "",
    gender: "",
    address: "",
    date_of_joining: getCurrentDateTime("YYYY-MM-DDTHH:mm"),
    role_id: "",
  };

  const [formData, setFormData] = useState(initialFormData);
  const [formErrors, setFormErrors] = useState({});

  useEffect(() => {
    if (mode === "edit" && manager) {
      setFormData({
        ...initialFormData,
        ...manager,
        date_of_joining: manager?.date_of_joining
          ? convertToUserTimezone(manager.date_of_joining, "YYYY-MM-DDTHH:mm")
          : "",
      });
    } else {
      setFormData(initialFormData);
    }
  }, [mode, manager, show]);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate phone number if provided
    const phoneValidation = validatePhoneNumber(formData.mobile);
    if (formData.mobile && !phoneValidation.isValid) {
      setFormErrors({ mobile: phoneValidation.error });
      return;
    }
    
    setFormErrors({});
    const mutation = mode === "add" ? createManager : updateManager;
    const successMessage =
      mode === "add"
        ? _t("manager_created_success", "Manager created successfully!")
        : _t("manager_updated_success", "Manager updated successfully!");

    try {
      const payload =
        mode === "add" ? formData : { id: manager.id, requestBody: formData };
      await mutation(payload).unwrap();
      // toast.success(successMessage);
      handleClose();
    } catch (error) {
      // toast.error('An error occurred. Please try again later.');
      console.error("Error creating/updating manager:", error);
    }
  };

  const handleClose = () => {
    setFormErrors({});
    setShow(false);
  };

  const renderFormFields = () => (
    <>
      <Col lg={6}>
        <InputWithLabel
          label={_t("manager_name", "Manager Name")}
          type="text"
          name="name"
          value={formData.name}
          onChange={handleChange}
          placeholder={_t("manager_name", "Manager Name")}
          required
        />
      </Col>
      <Col lg={6}>
        <InputWithLabel
          label={_t("email", "Email")}
          type="email"
          name="email"
          value={formData.email}
          onChange={handleChange}
          placeholder={_t("email", "Email")}
          required
        />
      </Col>
      <Col lg={6}>
        <InputWithLabel
          label={_t("phone_number", "Phone Number")}
          type="mobile"
          name="mobile"
          value={formData.mobile}
          onChange={handleChange}
          placeholder="e.g. +1 202-555-0143"
          required
          error={formErrors.mobile}
        />
      </Col>
      <Col lg={6}>
        <InputWithLabel
          label={_t("date_of_joining", "Date of Joining")}
          type="datetime-local"
          name="date_of_joining"
          value={formData.date_of_joining}
          onChange={handleChange}
          max={getCurrentDateTime("YYYY-MM-DDTHH:mm")}
          required
        />
      </Col>
      <Col lg={6}>
        <SelectBox
          label={_t("gender", "Gender")}
          name="gender"
          value={formData.gender}
          onChange={handleChange}
          options={[
            { value: "male", label: _t("male", "Male") },
            { value: "female", label: _t("female", "Female") },
          ]}
          required
        />
      </Col>
      <Col lg={6}>
        <InputWithLabel
          label={_t("address", "Address")}
          type="text"
          name="address"
          value={formData.address}
          onChange={handleChange}
          placeholder={_t("address", "Address")}
          required
        />
      </Col>
      <Col lg={6}>
        <SelectBox
          label={_t("role", "Role")}
          name="role_id"
          value={formData.role_id}
          onChange={handleChange}
          options={roleList?.data.map((list) => ({
            value: list.id,
            label: capitalize(_t(list.name)),
          }))}
          required
        />
      </Col>
    </>
  );

  return (
    <CommonModal
      show={show}
      handleClose={handleClose}
      setShow={setShow}
      title={
        mode === "add"
          ? _t("add_manager", "Add Manager")
          : _t("edit_manager", "Edit Manager")
      }
    >
      <Row>{renderFormFields()}</Row>
      <Row>
        <Col className="text-end">
          <Button
            label={
              mode === "add"
                ? _t("add_manager", "Add Manager")
                : _t("save_changes", "Save Changes")
            }
            size="small"
            onClick={handleSubmit}
            isLoading={isCreating || isUpdating}
          />
        </Col>
      </Row>
    </CommonModal>
  );
};

export default ManagerModal;
